import React from 'react';
import { Drawer, Spin, Timeline, Progress } from 'antd';
import { UserCheck, Store, ScanBarcode, Activity, Clock } from 'lucide-react';
import { fmtVZ } from '../../../utils/timeUtils';
import { semantic } from '../../../theme/tokens';
import { EmptyState } from '../../common';
import { useAuditorProfile } from './auditDrawers.controller';

function StatBar({ label, value, teamAvg }) {
    const high = value > 20;
    return (
        <div>
            <div className="mb-1 flex justify-between text-xs">
                <span className="text-surface-600">{label}</span>
                <span className={`font-semibold tabular-nums ${high ? 'text-danger-fg' : 'text-surface-900'}`}>{value}%</span>
            </div>
            <Progress percent={value} size="small" showInfo={false} strokeColor={high ? semantic.danger.solid : semantic.success.solid} className="!m-0" />
            {teamAvg !== undefined && <div className="text-xs text-surface-400 tabular-nums">Promedio equipo: {teamAvg}%</div>}
        </div>
    );
}

/** Number tile inside the drawer. */
function DrawerStat({ label, value, icon }) {
    return (
        <div className="rounded-xl border border-surface-200 bg-white p-4">
            <div className="flex items-center gap-1.5 text-[13px] font-medium text-surface-500">
                <span className="flex text-surface-400">{icon}</span>
                {label}
            </div>
            <div className="mt-1.5 text-2xl font-semibold tracking-tight text-surface-900 tabular-nums">{value ?? 0}</div>
        </div>
    );
}

export default function AuditAuditorDrawer({ open, onClose, userId, onFetchStats, onFetchActivity, onVisitClick, teamAverages = {} }) {
    const { loading, stats, timeline } = useAuditorProfile({ open, userId, onFetchStats, onFetchActivity });

    return (
        <Drawer title={stats?.user ? `Auditor — ${stats.user.name}` : 'Perfil de auditor'} width={560} open={open} onClose={onClose} destroyOnClose>
            {loading ? (
                <div className="flex justify-center py-12">
                    <Spin />
                </div>
            ) : !stats ? (
                <EmptyState icon={<UserCheck />} title="Sin datos del auditor" />
            ) : (
                <div className="space-y-6">
                    <p className="m-0 text-sm text-surface-500">
                        {stats.user?.email} · {stats.period}
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                        <DrawerStat label="Visitas" value={stats.visits} icon={<Store />} />
                        <DrawerStat label="Productos escaneados" value={stats.productsScanned} icon={<ScanBarcode />} />
                    </div>

                    <section>
                        <h3 className="m-0 mb-3 text-[15px] font-semibold text-surface-900">Tasas de error</h3>
                        <div className="space-y-3">
                            <StatBar label="Datos faltantes" value={stats.missingDataRate} teamAvg={teamAverages.missingDataRate} />
                            <StatBar label="Enriquecimiento pendiente" value={stats.pendingEnrichmentRate} teamAvg={teamAverages.pendingEnrichmentRate} />
                            <StatBar label="Fallo de imágenes" value={stats.imageFailureRate} teamAvg={teamAverages.imageFailureRate} />
                            <StatBar label="Códigos inválidos" value={stats.invalidBarcodeRate} teamAvg={teamAverages.invalidBarcodeRate} />
                            <StatBar label="Duplicados" value={stats.duplicateRate} teamAvg={teamAverages.duplicateRate} />
                        </div>
                        {stats.avgVisitDurationMs > 0 && (
                            <div className="mt-3 flex items-center gap-1.5 text-xs text-surface-500 tabular-nums">
                                <Clock className="text-surface-400" />
                                Tiempo promedio por visita: {Math.round(stats.avgVisitDurationMs / 60000)} min
                            </div>
                        )}
                    </section>

                    <section>
                        <h3 className="m-0 mb-3 flex items-center gap-2 text-[15px] font-semibold text-surface-900">
                            <Activity className="text-surface-400" />
                            Actividad reciente
                        </h3>
                        {timeline.length ? (
                            <Timeline
                                items={timeline.map((t) => ({
                                    color: 'blue',
                                    children: (
                                        <div className="group cursor-pointer" onClick={() => onVisitClick?.(t.visit)}>
                                            <div className="text-sm font-medium text-surface-900 group-hover:text-primary-600 transition-colors">
                                                {t.store?.name || 'Visita'}
                                            </div>
                                            <div className="text-xs text-surface-500 tabular-nums">
                                                {fmtVZ(t.visit?.createdAt)} · {t.scanCount} escaneos
                                            </div>
                                        </div>
                                    ),
                                }))}
                            />
                        ) : (
                            <EmptyState icon={<Activity />} title="Sin actividad" className="!py-6" />
                        )}
                    </section>
                </div>
            )}
        </Drawer>
    );
}
